/**
 * The render half of the admin audit-log page (HUB-115).
 *
 * Split from `AuditLog.tsx` for the same reason `usage-view.tsx` is: the
 * container imports `@/lib/hub-client`, which constructs a HubClient at module
 * load and throws under vitest. Every sentence comes from `audit-ui-model.ts`.
 *
 * Controlled and pure apart from one piece of local UI state: which row is
 * expanded. A row expands to its redacted body — the old → new diff block for
 * a reported write, then the raw JSON — because "what changed" is the whole
 * point of the page and must not need a second screen.
 */
import { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AuditEventListResponse, AuditEventView } from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { fmtDateTimeSeconds, truncateId } from '@/lib/format';
import { pageCountOf, pageIndexOf } from '@/lib/pagination';
import type { PrincipalLabels } from '@/lib/principal-labels';
import { cn } from '@/lib/utils';
import {
  AUDIT_BODY_TRUNCATED_COPY,
  AUDIT_PAGE_SIZE,
  AUDIT_SOURCE_OPTIONS,
  type AuditFilters,
  actionLabel,
  actorLabel,
  auditEmptyCopy,
  auditErrorCopy,
  auditPageOutOfRangeCopy,
  auditTotalSentence,
  bodyText,
  fieldChange,
  resolveSourceFilter,
} from './audit-ui-model';

export type AuditViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: AuditEventListResponse }
  | { kind: 'error'; httpStatus: number | null; message: string };

export interface AuditViewProps {
  state: AuditViewState;
  filters: AuditFilters;
  onFilter(patch: Partial<AuditFilters>): void;
  onPage(pageIndex: number): void;
  /** Registered tracks for the Track filter; empty until loaded. */
  trackOptions: ComboboxOption[];
  /** Hub users for the Actor filter; empty for a non-admin (GET /users is admin-only). */
  userOptions: ComboboxOption[];
  labels: PrincipalLabels;
}

const COLUMN_COUNT = 8;

/** "Any …" first, so clearing a pick is a pick, not a URL edit (the documents
 *  list's "All owners" idiom). */
const ANY_TRACK: ComboboxOption = { value: '', label: 'Any track' };
const ANY_USER: ComboboxOption = { value: '', label: 'Any user' };

/**
 * The exact-match action input commits on Enter or blur, not per keystroke:
 * the hub matches `route` by equality, so every intermediate value would fetch
 * a guaranteed-empty page and flicker the table through the empty state.
 */
function ActionInput({ value, onCommit }: { value: string; onCommit(value: string): void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    if (draft.trim() !== value) onCommit(draft.trim());
  };
  return (
    <Input
      id="audit-route"
      className="w-56 font-mono text-xs"
      placeholder="e.g. trial.updated"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
}

function AuditFilterBar({
  filters,
  onFilter,
  trackOptions,
  userOptions,
}: Pick<AuditViewProps, 'filters' | 'onFilter' | 'trackOptions' | 'userOptions'>) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <Label htmlFor="audit-source">Source</Label>
        <Select
          id="audit-source"
          className="w-40"
          value={resolveSourceFilter(filters.source)}
          onChange={(e) => onFilter({ source: e.target.value })}
        >
          {AUDIT_SOURCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="audit-track">Track</Label>
        <Combobox
          id="audit-track"
          className="w-56"
          value={filters.trackId}
          valueLabel={trackOptions.find((o) => o.value === filters.trackId)?.label}
          options={[ANY_TRACK, ...trackOptions]}
          onSelect={(value) => onFilter({ trackId: value })}
          placeholder="Any track"
          searchPlaceholder="Track name or id…"
          allowRawValue
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="audit-actor">Actor</Label>
        <Combobox
          id="audit-actor"
          className="w-64"
          value={filters.actorUserId}
          valueLabel={userOptions.find((o) => o.value === filters.actorUserId)?.label}
          options={[ANY_USER, ...userOptions]}
          onSelect={(value) => onFilter({ actorUserId: value })}
          placeholder="Any user"
          searchPlaceholder="Email or user id…"
          allowRawValue
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="audit-from">From</Label>
        <Input
          id="audit-from"
          type="date"
          className="w-40"
          value={filters.from}
          onChange={(e) => onFilter({ from: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="audit-to">To</Label>
        <Input
          id="audit-to"
          type="date"
          className="w-40"
          value={filters.to}
          onChange={(e) => onFilter({ to: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        {/* The hub matches `route` by equality (HUB-063 §4), so the label says so. */}
        <Label htmlFor="audit-route">Action (exact)</Label>
        <ActionInput value={filters.route} onCommit={(route) => onFilter({ route })} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: number }) {
  const variant = status < 300 ? 'success' : status < 400 ? 'muted' : 'destructive';
  return (
    <Badge variant={variant} className="tabular-nums">
      {status}
    </Badge>
  );
}

/** Label above, raw id below — same idiom as the approvals queue. */
function LabelledId({ label, id }: { label: string | undefined; id: string }) {
  return (
    <div className="flex flex-col leading-tight" title={id}>
      {label ? <span className="text-xs">{label}</span> : null}
      <span className="font-mono text-[11px] text-muted-foreground">{truncateId(id)}</span>
    </div>
  );
}

function FieldChangeBlock({ row }: { row: AuditEventView }) {
  const change = fieldChange(row);
  if (!change) return null;
  return (
    <div className="grid grid-cols-[minmax(8rem,1fr)_2fr_2fr] gap-x-6 gap-y-1 text-sm" data-testid="audit-field-change">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Field</span>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Old value</span>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">New value</span>
      <code className="font-mono text-xs">{change.field}</code>
      <span className="whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground line-through decoration-muted-foreground/60">
        {change.oldValue}
      </span>
      <span className="whitespace-pre-wrap break-words font-mono text-xs">{change.newValue}</span>
    </div>
  );
}

function ExpandedBody({ row }: { row: AuditEventView }) {
  const json = bodyText(row);
  return (
    <div className="space-y-3 px-2 py-1">
      {row.bodyTruncated && (
        <Alert variant="warning" data-testid="audit-body-truncated">
          {AUDIT_BODY_TRUNCATED_COPY}
        </Alert>
      )}
      <FieldChangeBlock row={row} />
      {json === null ? (
        <p className="text-xs text-muted-foreground">No body snapshot recorded for this event.</p>
      ) : (
        <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 font-mono text-[11px] leading-snug">{json}</pre>
      )}
    </div>
  );
}

function AuditRow({
  row,
  labels,
  expanded,
  onToggle,
}: {
  row: AuditEventView;
  labels: PrincipalLabels;
  expanded: boolean;
  onToggle(): void;
}) {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <Fragment>
      <TableRow
        className={cn('cursor-pointer', expanded && 'bg-muted/40')}
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid="audit-row"
      >
        <TableCell className="w-6 pr-0">
          <Chevron className="size-4 opacity-60" aria-hidden="true" />
        </TableCell>
        <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">{fmtDateTimeSeconds(row.createdAt)}</TableCell>
        <TableCell>
          {row.actorUserId ? (
            <LabelledId label={labels.userEmail(row.actorUserId) ?? row.actorEmail ?? undefined} id={row.actorUserId} />
          ) : (
            <span className="text-xs">{actorLabel(row)}</span>
          )}
        </TableCell>
        <TableCell>
          <Badge variant="outline">{row.source}</Badge>
        </TableCell>
        <TableCell>
          {row.trackId ? (
            <LabelledId label={labels.trackName(row.trackId)} id={row.trackId} />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell>
          <code className="font-mono text-xs">{actionLabel(row)}</code>
        </TableCell>
        <TableCell>
          <code className="font-mono text-xs text-muted-foreground">{row.httpPath}</code>
        </TableCell>
        <TableCell>
          <StatusBadge status={row.httpStatus} />
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={COLUMN_COUNT} className="bg-muted/20">
            <ExpandedBody row={row} />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function AuditTable({
  data,
  filters,
  labels,
  onPage,
}: {
  data: AuditEventListResponse;
  filters: AuditFilters;
  labels: PrincipalLabels;
  onPage(pageIndex: number): void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pageIndex = pageIndexOf(filters.page);
  const pageCount = pageCountOf(data.total, AUDIT_PAGE_SIZE);
  // Rows exist but none on this page: the page index is stale, not the filters.
  const pastTheEnd = data.events.length === 0 && data.total > 0;

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-6" />
              <TableHead>Time (UTC)</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Track</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Record</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-center text-muted-foreground" data-testid="audit-empty">
                  {pastTheEnd ? auditPageOutOfRangeCopy(data.total) : auditEmptyCopy(resolveSourceFilter(filters.source))}
                </TableCell>
              </TableRow>
            ) : (
              data.events.map((row) => (
                <AuditRow
                  key={row.id}
                  row={row}
                  labels={labels}
                  expanded={expandedId === row.id}
                  onToggle={() => setExpandedId((current) => (current === row.id ? null : row.id))}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground" data-testid="audit-total">
          {auditTotalSentence(data.total, pageIndex)}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onPage(pastTheEnd ? 0 : pageIndex - 1)}
            disabled={pageIndex === 0}
          >
            Prev
          </Button>
          <Button size="sm" variant="outline" onClick={() => onPage(pageIndex + 1)} disabled={pageIndex + 1 >= pageCount}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AuditView({ state, filters, onFilter, onPage, trackOptions, userOptions, labels }: AuditViewProps) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <p className="text-sm text-muted-foreground">
          Who did what, when, and to which record — one row per recorded event. Expand a row for the old and new value.
        </p>
      </div>

      <AuditFilterBar filters={filters} onFilter={onFilter} trackOptions={trackOptions} userOptions={userOptions} />

      {state.kind === 'loading' && <p className="text-sm text-muted-foreground">Loading…</p>}
      {state.kind === 'error' && (
        <Alert data-testid="audit-error">{auditErrorCopy(state.httpStatus, state.message)}</Alert>
      )}
      {state.kind === 'ready' && <AuditTable data={state.data} filters={filters} labels={labels} onPage={onPage} />}
    </div>
  );
}
